<template>
  <div class="fixed flex items-center justify-center z-50 papsi-modal-overlay" style="z-index: 55;"
    :style="{ backgroundColor: overlay_enabled ? overlay_background : 'transparent' }" @click.self="closeModal">
    <div class="keyboard-container bg-white rounded-xl p-12 w-2/3 relative">
      <slot></slot>

      <!-- Input Field -->
      <div class="flex justify-center items-center w-full relative">
        <input type="text" :value="displayInput" :placeholder="placeholder" :max_length="max_length"
          class="input-field p-8 text-5xl rounded-xl h-24" readonly />
        <div v-if="password" class="absolute papsi-toggle" @click="togglePasswordVisibility">
          <img :src="getIcon(show_password ? 'password_visible' : 'password_invisible')" width="40rem" />
        </div>
      </div>

      <!-- Keyboard -->
      <div class="keyboard mt-10">
        <!-- First Row: Numbers and Special Characters -->

        <div class="keyboard-row" v-if="selected_input_type !== 'serial' && selected_input_type !== 'account'">
          <button v-for="char in additionalSpecialChars" :key="char" @click="addCharacter(char)">
            {{ char }}
          </button>
        </div>

        <div class="keyboard-row">
          <button v-for="char in numbers" :key="char" @click="addCharacter(char)">
            {{ char }}
          </button>

          <div v-if="selected_input_type === 'serial'">
            <button @click="addCharacter('-')">-</button>
          </div>
          <button v-if="selected_input_type !== 'serial' && selected_input_type !== 'account'" @click="addCharacter('_')">_</button>

          <button @click="removeLastCharacter" class="backspace-key">
            <img :src="getIcon('backspace')" width="60rem">
          </button>
        </div>

        <!-- Second Row: Letters and Special Characters -->
        <div class="keyboard-row">
          <button v-for="char in row1" :key="char" @click="addCharacter(getCharacter(char))">
            {{ getCharacter(char) }}
          </button>
          <div v-if="selected_input_type !== 'serial' && selected_input_type !== 'account'">
            <button v-for="char in ['@', '.']" :key="char" @click="addCharacter(char)" style="margin-right: 15px;">
              {{ char }}
            </button>
          </div>
        </div>

        <!-- Third Row: Letters and Enter Key -->
        <div class="keyboard-row">
          <button v-if="selected_input_type !== 'serial' && selected_input_type !== 'account'" @click="toggleCapsLock" :class="{ 'caps-on': caps_lock }" style="width: 9rem;" class="caps">
            Caps
          </button>
          <button v-for="char in row2" :key="char" @click="addCharacter(getCharacter(char))">
            {{ getCharacter(char) }}
          </button>
          <button @click="submitInput" :disabled="!user_input || loading" class="enter-key">
            <div class="flex flex-row justify-center items-center">
              <p>Enter</p>
            </div>
          </button>
        </div>

        <!-- Fourth Row: Letters with Additional Special Characters -->
        <div class="keyboard-row">
          <button v-for="char in filteredRow3" :key="char" @click="addCharacter(getCharacter(char))">
            {{ getCharacter(char) }}
          </button>
          <button v-if="is_email && !is_account" @click="addCharacter('.com')" class="com" style="width: 9rem;">
            .com
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mixin from "@/mixins/controls.js";

export default {
  name: "v-keyboard",
  mixins: [mixin],
  props: {
    is_caps: {
      type: Boolean,
      default: false, // Default is now false
    },
    is_account: {
      type: Boolean,
      default: false,
    },
    is_email: {
      type: Boolean,
      default: false,
    },
    password: {
      type: Boolean,
      default: false,
    },
    overlay_enabled: {
      type: Boolean,
      default: true,
    },
    overlay_background: {
      type: String,
      default: "rgba(0, 0, 0, 0.7)",
    },
    placeholder: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    max_length: {
      type: Number,
      required: false,
    },
    selected_input_type: {
      type: String,
      required: false,
    },
    inputted_value: {
      type: String,
      default: "",
      required: false,
    }
  },
  data() {
    return {
      beep: new Audio('https://resource.pafcpic.net/assets/pia/sounds/papsi_beep.mp3'),
      user_input: this.inputted_value,
      numbers: "1234567890".split(""),
      row1: "qwertyuiop".split(""),
      row2: "asdfghjkl".split(""),
      row3: "zxcvbnñm".split(""), // Include ñ initially
      additionalSpecialChars: "!#$%^&*+=/".split(""), // New special characters
      show_password: false,
      caps_lock: this.is_caps, // Create a local copy of `is_caps`
    };
  },
  computed: {
    filteredRow3() {
      // Exclude ñ when `is_account` is true
      return this.is_account ? this.row3.filter((char) => char !== "ñ") : this.row3;
    },
    displayInput() {
      return this.password && !this.show_password
        ? "*".repeat(this.user_input.length)
        : this.user_input;
    },
  },
  methods: {
    playBeep() {
        this.beep.play();
        
    },
    addCharacter(char) {
      // Allow dash only for serial numbers
      this.playBeep();
      
      if (char === '-' && this.selected_input_type !== 'serial') {
        return; // Prevent adding dash for account number
      }
      if (!this.max_length || this.user_input.length < this.max_length) {
        this.user_input += char;
      }
    },
    removeLastCharacter() {
      this.playBeep();
      this.user_input = this.user_input.slice(0, -1);
    },
    submitInput() {
      this.playBeep();
      this.$emit("submit", this.user_input);
    },
    closeModal() {
      this.$emit("close");
    },
    getCharacter(char) {
      return this.caps_lock ? char.toUpperCase() : char;
    },
    togglePasswordVisibility() {
      this.show_password = !this.show_password;
    },
    toggleCapsLock() {
      this.playBeep();
      this.caps_lock = !this.caps_lock;
    },
  },
};
</script>

<style scoped>
.keyboard-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.input-field {
  text-align: start;
  border: 2px solid #ccc;
  width: 100rem;
  outline: none;
}

.keyboard {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.keyboard-row {
  display: flex;
  gap: 15px;
  justify-content: center;
}

button {
  padding: 15px;
  width: 4.5rem;
  height: 5.5rem;
  font-size: 2.5rem;
  font-weight: 500;
  background-color: #F4F5FB;
  border: 1px solid #ccc;
  border-radius: 10px;
}

button.caps-on {
  background-color: #FFC107; /* Change to desired color when Caps Lock is on */
  color: white;
}

.backspace-key {
  background-color: #E5413E;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 15px;
}

.enter-key {
  background-color: #016B00;
  color: white;
  padding: 10px;
  text-align: center;
  width: 9rem;
  cursor: pointer;
}

.enter-key:disabled {
  background-color: #b8c2cc;
  cursor: not-allowed;
}

.papsi-modal-overlay {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
}

.papsi-close {
  top: 2.5rem;
  right: 2.5rem;
}

.papsi-toggle {
  right: 1.5rem;
  /* Adjust as needed for spacing */
  top: 50%;
  transform: translateY(-50%);
}

@media (min-width: 2500px) {
  button {padding: 25px; width: 6.5rem; height: 7.5rem; font-size: 3.5rem; border-radius: 1rem;}
  .keyboard-row, .keyboard {gap: 20px;}
  .enter-key, .caps, .com {width: 12rem !important;}
  .h-24 {height: 7rem;}

}

</style>